/**
 * Today view - shows current day, events, tasks, quick-add, and swipe navigation.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CreateEventInput, CreateTaskInput } from '@anna-calendar/shared';
import { useEventsStore } from '../store/events';
import { useTasksStore } from '../store/tasks';
import { useSwipeGesture } from '../hooks/useSwipe';
import { EventModal } from '../components/EventModal';
import { TaskModal } from '../components/TaskModal';
import { formatDateISO, formatDateTime, formatTime, addDays, todayISO, priorityLabel, priorityColor } from '../utils/dates';
import { expandEvents } from '../utils/recurrence';

export function TodayView() {
  const [currentDate, setCurrentDate] = useState(todayISO());
  const [showEventModal, setShowEventModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const { events, fetchEvents, createEvent } = useEventsStore();
  const { tasks, fetchTasks, createTask, updateTask } = useTasksStore();

  const dayDate = new Date(currentDate + 'T12:00:00');
  const dayOfWeek = dayDate.toLocaleDateString('en-US', { weekday: 'long' });
  const dateStr = dayDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const loadDay = useCallback(async (date: string) => {
    const d = new Date(date + 'T12:00:00');
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).toISOString();
    // Fetch events with a wide lookback to capture recurring events whose
    // original start_time predates this day but whose RRULE generates
    // occurrences that fall on it.
    const lookback = new Date(d.getFullYear() - 1, d.getMonth(), d.getDate()).toISOString();
    await fetchEvents({ start: lookback, end });
    await fetchTasks({ due_date: date });
  }, [fetchEvents, fetchTasks]);

  useEffect(() => {
    loadDay(currentDate);
  }, [currentDate, loadDay]);

  // Swipe handlers for day navigation
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: () => setCurrentDate((d) => addDays(d, 1)),
    onSwipeRight: () => setCurrentDate((d) => addDays(d, -1)),
    threshold: 50,
  });

  // Expand recurring events into visible occurrences for this day.
  // The day range is midnight-to-midnight in local time.
  const dayRangeStart = new Date(currentDate + 'T00:00:00').toISOString();
  const dayRangeEnd = new Date(new Date(currentDate + 'T12:00:00').getTime() + 86400000).toISOString();

  const todayEvents = useMemo(() => {
    const expanded = expandEvents(events, dayRangeStart, dayRangeEnd);
    return expanded
      .filter((e) => formatDateISO(new Date(e.start_time)) === currentDate)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [events, dayRangeStart, dayRangeEnd, currentDate]);

  const todayTasks = tasks
    .filter((t) => t.due_date === currentDate)
    .sort((a, b) => b.priority - a.priority || (a.due_time || '').localeCompare(b.due_time || ''));

  const isToday = currentDate === todayISO();

  const handleQuickAddTask = async () => {
    setShowTaskModal(true);
  };

  const handleQuickAddEvent = () => {
    setShowEventModal(true);
  };

  const handleCreateEvent = async (data: CreateEventInput) => {
    await createEvent(data);
    loadDay(currentDate);
  };

  const handleCreateTask = async (data: CreateTaskInput) => {
    await createTask(data);
    loadDay(currentDate);
  };

  const handleToggleTask = async (id: string, completed: number) => {
    await updateTask(id, { completed: completed ? 0 : 1 });
  };

  return (
    <div
      className="h-full overflow-y-auto p-4 desktop-pb"
      {...swipeHandlers}
    >
      {/* Day header with navigation */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {dayOfWeek}
          </h2>
          <p className="text-base mt-1" style={{ color: 'var(--text-secondary)' }}>
            {dateStr}
          </p>
        </div>
        {!isToday && (
          <button
            onClick={() => setCurrentDate(todayISO())}
            className="px-3 py-2 rounded-lg text-sm font-medium"
            style={{
              backgroundColor: 'var(--accent-soft)',
              color: 'var(--accent)',
              minHeight: '44px',
            }}
          >
            Today
          </button>
        )}
      </div>

      {/* Day nav arrows (visible on desktop, swipe on mobile) */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setCurrentDate((d) => addDays(d, -1))}
          className="px-3 py-2 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-secondary)',
            minHeight: '44px',
            minWidth: '44px',
          }}
          aria-label="Previous day"
        >
          &larr;
        </button>
        <button
          onClick={() => setCurrentDate((d) => addDays(d, 1))}
          className="px-3 py-2 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-secondary)',
            minHeight: '44px',
            minWidth: '44px',
          }}
          aria-label="Next day"
        >
          &rarr;
        </button>
      </div>

      {/* Events section */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Events
          </h3>
          <button
            onClick={handleQuickAddEvent}
            className="px-3 py-2 rounded-lg text-sm font-medium"
            style={{
              backgroundColor: 'var(--accent)',
              color: '#fff',
              minHeight: '44px',
            }}
          >
            + Event
          </button>
        </div>
        {todayEvents.length === 0 ? (
          <p className="text-sm py-4 text-center" style={{ color: 'var(--text-muted)' }}>
            No events {isToday ? 'today' : 'this day'}
          </p>
        ) : (
          <div className="space-y-2">
            {todayEvents.map((event) => (
              <div
                key={event.id}
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  borderLeft: `4px solid ${event.color}`,
                }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {event.title}
                  </p>
                  {!event.all_day && (
                    <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {formatDateTime(event.start_time)}
                      {event.end_time && ` - ${formatDateTime(event.end_time)}`}
                    </p>
                  )}
                  {event.all_day === 1 && (
                    <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>All day</p>
                  )}
                  {event.recurrence_rule && (
                    <span className="text-xs mt-0.5 inline-block" style={{ color: 'var(--accent)' }}>
                      Recurring
                    </span>
                  )}
                </div>
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: event.color }}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Tasks section */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Tasks
          </h3>
          <button
            onClick={handleQuickAddTask}
            className="px-3 py-2 rounded-lg text-sm font-medium"
            style={{
              backgroundColor: 'var(--accent)',
              color: '#fff',
              minHeight: '44px',
            }}
          >
            + Task
          </button>
        </div>
        {todayTasks.length === 0 ? (
          <p className="text-sm py-4 text-center" style={{ color: 'var(--text-muted)' }}>
            No tasks {isToday ? 'today' : 'this day'}
          </p>
        ) : (
          <div className="space-y-2">
            {todayTasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{ backgroundColor: 'var(--bg-tertiary)' }}
              >
                <button
                  onClick={() => handleToggleTask(task.id, task.completed)}
                  className="w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0"
                  style={{
                    borderColor: task.completed ? 'var(--success)' : 'var(--border-color)',
                    backgroundColor: task.completed ? 'var(--success)' : 'transparent',
                    minHeight: '44px',
                    minWidth: '44px',
                    width: '44px',
                    height: '44px',
                  }}
                  aria-label={task.completed ? 'Mark incomplete' : 'Mark complete'}
                >
                  {task.completed ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : null}
                </button>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-base truncate"
                    style={{
                      color: task.completed ? 'var(--text-muted)' : 'var(--text-primary)',
                      textDecoration: task.completed ? 'line-through' : 'none',
                    }}
                  >
                    {task.title}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {task.due_time && (
                      <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        {formatTime(task.due_time)}
                      </span>
                    )}
                    <span
                      className="text-xs px-1.5 py-0.5 rounded"
                      style={{
                        color: priorityColor(task.priority),
                        backgroundColor: 'var(--bg-primary)',
                      }}
                    >
                      {priorityLabel(task.priority)}
                    </span>
                    {task.recurrence_rule && (
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        Recurring
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Floating quick-add button (mobile) */}
      <button
        onClick={handleQuickAddEvent}
        className="fixed bottom-20 right-4 w-14 h-14 rounded-full flex items-center justify-center shadow-lg mobile-only z-30"
        style={{
          backgroundColor: 'var(--accent)',
          color: '#fff',
        }}
        aria-label="Quick add"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>

      {/* Modals */}
      <EventModal
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        onSave={handleCreateEvent}
        defaultDate={currentDate}
      />
      <TaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        onSave={handleCreateTask}
        defaultDate={currentDate}
      />
    </div>
  );
}
